import ChannelManager, { ChannelType } from '../channel-manager'
import ShopInformationManager from '../shop-information-manager/ShopInformationManager'
import { ShopInformationDataSourceManager } from '../shop-information-manager/ShopInformationDataSourceManager'
import LocalStorageManager from '../local-storage-manager'
import QRCodeType from '@/views/app/utils/path-resolver/models/QRCodeType'

/**
 * 登录要求检查器
 * 负责判断是否需要强制用户登录
 */
export default class LoginRequirementChecker {
  private static _instance: LoginRequirementChecker
  private channelManager: ChannelManager
  private shopInfoManager: ShopInformationManager
  private shopDataSourceManager: ShopInformationDataSourceManager
  private localStorageManager: LocalStorageManager

  private constructor() {
    this.channelManager = ChannelManager.instance()
    this.shopInfoManager = ShopInformationManager.instance()
    this.shopDataSourceManager = ShopInformationDataSourceManager.instance()
    this.localStorageManager = new LocalStorageManager()
  }

  static instance(): LoginRequirementChecker {
    if (!this._instance) {
      this._instance = new LoginRequirementChecker()
    }
    return this._instance
  }

  /**
   * 获取URL查询参数
   * @param key 参数名
   * @returns 参数值
   */
  private getQueryParam(key: string): string {
    const reg = new RegExp(`(^|&)${key}=([^&]*)(&|$)`, 'i')
    const r = window.location.search.substring(1).match(reg)
    return r ? r[2] : ''
  }

  /**
   * 检查特殊情况是否需要跳过登录检查
   * @returns true表示应该跳过检查，false表示需要进行检查
   */
  private shouldSkipLoginCheck(): boolean {
    const qrCodeType = Number(this.getQueryParam('t'))

    // 特殊情况：团购券(GroupCoupon)不需要强制登录
    if (qrCodeType === QRCodeType.GroupCoupon) {
      return true
    }

    // 可以在这里添加更多特殊情况
    // if (qrCodeType === QRCodeType.XXX) {
    //   return true
    // }

    return false
  }

  /**
   * 判断用户是否已登录
   * @returns 是否已登录
   */
  private isUserLoggedIn(): boolean {
    return this.localStorageManager.userId !== ''
  }

  /**
   * 判断店铺是否只有Circle Eats渠道
   * @param channels 店铺渠道列表
   * @returns 是否只有Circle Eats渠道
   */
  private isOnlyCircleEatsChannel(channels?: number[]): boolean {
    return (
      channels !== undefined &&
      channels.length === 1 &&
      channels[0] === ChannelType.CIRCLE_EATS
    )
  }

  /**
   * 检查是否需要强制登录
   * @param shopId 店铺ID
   * @returns Promise<boolean> true表示需要强制登录
   */
  async shouldRequireLogin(shopId: string): Promise<boolean> {
    try {
      // console.log(`[LoginRequirementChecker] 检查店铺 ${shopId} 是否需要强制登录`)

      // 特殊情况检查：某些场景下跳过登录检查
      if (this.shouldSkipLoginCheck()) {
        // console.log(`[LoginRequirementChecker] 特殊情况，跳过登录检查`)
        return false
      }

      // 如果用户已登录，不需要检查
      if (this.isUserLoggedIn()) {
        return false
      }

      // console.log(`[LoginRequirementChecker] 用户未登录，获取店铺 ${shopId} 信息进行检查`)
      // 获取店铺基础信息和详细信息
      const [shopInfo] = await Promise.all([
        this.shopInfoManager.fetchShopInformation(shopId, false),
        // 获取基础信息以确保channels被存储到ChannelManager
        new Promise<void>((resolve, reject) => {
          this.shopDataSourceManager
            .fetchShopInformation(shopId, () => {
              resolve()
            })
            .catch(reject)
        })
      ])

      // 从ChannelManager获取channels（已由shopDataSourceManager存储）
      const channels = this.channelManager.getShopChannels(shopId)

      // 判断是否只有Circle Eats渠道
      const isOnlyCircleEats = this.isOnlyCircleEatsChannel(channels)

      // 判断是否需要登录
      const needLoginToOrder = shopInfo.mobileOrderSetting.isNeedLoginToOrder === true
      // const needLoginToOrder = true

      // console.log(`[LoginRequirementChecker] 店铺 ${shopId} 渠道: ${channels}, 仅Circle Eats: ${isOnlyCircleEats}, 下单需登录: ${needLoginToOrder}`)

      // 返回是否需要强制登录
      const result = isOnlyCircleEats && needLoginToOrder
      return result
    } catch (error) {
      // 如果获取信息失败，不阻塞用户操作
      return false
    }
  }
}
